import {
  FE_TYPE_TO_BACKEND,
  isEdgeDerivedInputField,
  type YDocWriteValue,
  YDOC_NODE_FIELDS,
} from '../../generated/nodeDataTypes'

const FRONTEND_WRITABLE_OUTPUT_MAP_PORTS = new Set([
  'audio.out_audio',
  'image.out_image',
  'video.out_video',
])

export type AssetPortWriteData = Readonly<Record<string, YDocWriteValue>>
type AssetPortWriteInput = AssetPortWriteData | undefined

function toBackendNodeType(nodeType: string | undefined): string | undefined {
  if (!nodeType) return undefined
  return FE_TYPE_TO_BACKEND[nodeType] ?? nodeType
}

export function isEdgeBackedAssetInputPort(
  nodeType: string | undefined,
  field: string,
): boolean {
  return isEdgeDerivedInputField(nodeType, field)
}

export function getDirectAssetInputPortWrites(
  nodeType: string | undefined,
  updates: AssetPortWriteInput,
): string[] {
  if (!updates) return []
  return Object.keys(updates).filter((field) => isEdgeBackedAssetInputPort(nodeType, field))
}

export function stripDirectAssetInputPortWrites<T extends Record<string, YDocWriteValue>>(
  nodeType: string | undefined,
  updates: T,
): { data: T; strippedFields: string[] } {
  const strippedFields = getDirectAssetInputPortWrites(nodeType, updates)
  if (strippedFields.length === 0) {
    return { data: updates, strippedFields }
  }

  const sanitized = { ...updates }
  for (const field of strippedFields) {
    delete sanitized[field]
  }
  return { data: sanitized, strippedFields }
}

export function isBackendAuthoredOutputMapPort(
  nodeType: string | undefined,
  field: string,
): boolean {
  if (!field.startsWith('out_')) return false

  const backendType = toBackendNodeType(nodeType)
  if (!backendType) return false

  const spec = YDOC_NODE_FIELDS[backendType]?.[field]
  if (!spec || spec.wireType !== 'map') return false
  return !FRONTEND_WRITABLE_OUTPUT_MAP_PORTS.has(`${backendType}.${field}`)
}

export function getBackendAuthoredOutputMapWrites(
  nodeType: string | undefined,
  updates: AssetPortWriteInput,
): string[] {
  if (!updates) return []
  return Object.keys(updates).filter((field) => isBackendAuthoredOutputMapPort(nodeType, field))
}

export function assertNoBackendAuthoredOutputMapWrites(
  nodeType: string | undefined,
  nodeId: string,
  updates: AssetPortWriteInput,
  writer: string,
): void {
  const blockedFields = getBackendAuthoredOutputMapWrites(nodeType, updates)
  if (blockedFields.length === 0) return

  const backendType = toBackendNodeType(nodeType) ?? 'unknown'
  throw new Error(
    `${writer} cannot write backend-authored output map port(s) ${blockedFields.join(', ')} ` +
      `on ${backendType} node ${nodeId}. Use a narrow Y.Map patch for UI-only leaf updates ` +
      'or forceDesignerJsonFields during import/restore.',
  )
}

export function assertNoDirectAssetInputPortWrites(
  nodeType: string | undefined,
  nodeId: string,
  updates: AssetPortWriteInput,
  writer: string,
): void {
  const blockedFields = getDirectAssetInputPortWrites(nodeType, updates)
  if (blockedFields.length === 0) return

  const backendType = toBackendNodeType(nodeType) ?? 'unknown'
  throw new Error(
    `${writer} cannot write edge-derived input port(s) ${blockedFields.join(', ')} ` +
      `on ${backendType} node ${nodeId}. Use replacePortEdges() so the value stays ` +
      'bound to its source node.',
  )
}
